import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast, Toaster } from 'sonner';
import { Plus, Edit2, Trash2, X, Save, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const categories = ['General', 'Pricing', 'Technical', 'Security', 'Support', 'Features'];

const AdminFAQsPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'General',
    order: 0
  });

  useEffect(() => { loadFAQs(); }, []);

  const loadFAQs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/cms/faqs?active_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setFaqs(data);
    } catch (e) {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingId 
        ? `${API_URL}/api/cms/faqs/${editingId}`
        : `${API_URL}/api/cms/faqs`;
      
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) throw new Error('Failed to save');
      toast.success(editingId ? 'FAQ updated' : 'FAQ created');
      resetForm();
      loadFAQs();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/cms/faqs/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('FAQ deleted');
      loadFAQs();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (faq) => {
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || 'General',
      order: faq.order || 0
    });
    setEditingId(faq.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ question: '', answer: '', category: 'General', order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  // Group FAQs by category
  const groupedFAQs = faqs.reduce((acc, faq) => {
    const cat = faq.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {});

  if (loading) return <div className="text-text-secondary">Loading...</div>;

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">FAQs</h1>
          <p className="text-text-secondary mt-1">Manage frequently asked questions</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-tech-blue hover:bg-tech-blue/90">
          <Plus size={18} className="mr-2" />
          Add FAQ
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl border-border-line my-8">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle>{editingId ? 'Edit' : 'Add'} FAQ</CardTitle>
              <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
                <X size={20} />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Question *</label>
                  <input
                    type="text"
                    required
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    placeholder="e.g., What makes GetCare different?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Answer *</label>
                  <textarea
                    required
                    rows={5}
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                    placeholder="Provide a detailed answer..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Display Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                    <Save size={18} className="mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* FAQs List by Category */}
      {Object.entries(groupedFAQs).map(([category, categoryFaqs]) => (
        <div key={category}>
          <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center">
            <span className="w-2 h-2 rounded-full bg-tech-blue mr-2"></span>
            {category}
            <span className="ml-2 text-sm font-normal text-text-secondary">({categoryFaqs.length})</span>
          </h2>
          <div className="space-y-3">
            {categoryFaqs.map((faq) => (
              <Card key={faq.id} className="border-border-line">
                <CardContent className="p-4">
                  <div 
                    className="flex items-start justify-between cursor-pointer"
                    onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}
                  >
                    <div className="flex items-start space-x-3 flex-1">
                      <HelpCircle className="text-tech-blue flex-shrink-0 mt-1" size={20} />
                      <div className="flex-1">
                        <h3 className="font-semibold text-text-primary">{faq.question}</h3>
                        {expandedId === faq.id && (
                          <p className="mt-2 text-text-secondary text-sm leading-relaxed">{faq.answer}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <span className="text-xs bg-page-bg text-text-secondary px-2 py-1 rounded">#{faq.order}</span>
                      {expandedId === faq.id ? (
                        <ChevronUp className="text-text-secondary" size={20} />
                      ) : (
                        <ChevronDown className="text-text-secondary" size={20} />
                      )}
                    </div>
                  </div>
                  {expandedId === faq.id && (
                    <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border-line">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(faq); }}>
                        <Edit2 size={14} className="mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(faq.id); }} className="text-red-500 hover:text-red-600">
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {faqs.length === 0 && (
        <div className="text-center py-12 text-text-secondary">
          <HelpCircle className="mx-auto mb-4" size={48} />
          <p>No FAQs yet. Add your first question.</p>
        </div>
      )}
    </div>
  );
};

export default AdminFAQsPage;
